import { useState } from 'react';
import { 
  User, Bell, Key, Shield, Globe, Save, LogOut, Loader2, 
  Camera, Link as LinkIcon, UserCheck, CreditCard, Check, 
  Zap, Crown, Download
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { sileo } from 'sileo';
import { Header } from '../../widgets/header';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Badge } from '../../shared/ui';
import { useCurrentUser, useUpdateProfile, useInvoices, useUpgradePlan } from '../../shared/api';
import { useAuth } from '../../features/auth-flow';
import { generateInvoicePdf, InvoiceData } from '../../features/export-report';

/**
 * Settings Page - User profile and application configuration
 * Sections: Profile, Notifications, Billing, API Keys, Security, Preferences
 */

type SettingsTab = 'profile' | 'notifications' | 'billing' | 'api-keys' | 'security' | 'preferences';

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const [selectedSection, setSelectedSection] = useState<SettingsTab>('profile');

  // Real API data
  const { data: currentUser, isLoading: isUserLoading } = useCurrentUser();
  const updateProfile = useUpdateProfile();
  const { logout } = useAuth();

  // Billing Data & Mutations
  const { data: invoices, isLoading: isInvoicesLoading } = useInvoices();
  const upgradePlan = useUpgradePlan();

  // Local form state (initialized from API data)
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    company: '',
    githubToken: '',
    geminiApiKey: '',
    geminiModel: 'gemini-3-flash',
    role: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // Avatar URL state (separate for live preview)
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [showAvatarInput, setShowAvatarInput] = useState(false);

  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    criticalIssues: true,
    weeklyReports: true,
    newFeatures: false,
  });

  // Sync form data when API data loads
  const [synced, setSynced] = useState(false);
  if (currentUser && !synced) {
    setUserData({
      name: currentUser.full_name,
      email: currentUser.email,
      company: '',
      githubToken: currentUser.github_token ?? '',
      geminiApiKey: currentUser.gemini_api_key ?? '',
      geminiModel: currentUser.gemini_model ?? 'gemini-3-flash',
      role: currentUser.tier,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    setAvatarUrl(currentUser.avatar_url ?? '');
    setSynced(true);
  }

  const sections = [
    { id: 'profile' as const, label: t('settings.sections.profile'), icon: User },
    { id: 'notifications' as const, label: t('settings.sections.notifications'), icon: Bell },
    { id: 'billing' as const, label: 'Billing & Plans', icon: CreditCard },
    { id: 'api-keys' as const, label: t('settings.sections.apiKeys'), icon: Key },
    { id: 'security' as const, label: t('settings.sections.security'), icon: Shield },
    { id: 'preferences' as const, label: t('settings.sections.preferences'), icon: Globe },
  ];

  const handleSaveProfile = async () => {
    try {
      const payload: { 
        full_name: string; 
        avatar_url?: string; 
        github_token?: string;
        gemini_api_key?: string;
        gemini_model?: string;
      } = {
        full_name: userData.name,
        github_token: userData.githubToken,
        gemini_api_key: userData.geminiApiKey,
        gemini_model: userData.geminiModel,
      };
      // Include avatar_url if it has been set
      if (avatarUrl.trim()) {
        payload.avatar_url = avatarUrl.trim();
      }
      await updateProfile.mutateAsync(payload);
      setShowAvatarInput(false);

      sileo.success({
        title: t('toasts.profileSaved'),
        description: t('toasts.profileSavedDesc'),
        icon: <UserCheck className="size-3.5" />,
      });
    } catch (err) {
      console.error('Failed to update profile:', err);
      sileo.error({
        title: t('toasts.profileError'),
        description: t('toasts.profileErrorDesc'),
      });
    }
  };

  const handleUpgrade = async (plan: 'pro' | 'enterprise') => {
    try {
      await upgradePlan.mutateAsync(plan);
      sileo.success({
        title: 'Plan Updated',
        description: `Your account has been upgraded to ${plan.toUpperCase()} successfully.`,
        icon: <Zap className="size-3.5" />,
      });
    } catch (err) {
      sileo.error({
        title: 'Upgrade Failed',
        description: 'There was an error processing your request.',
      });
    }
  };

  const handleDownloadInvoice = (inv: any) => {
    try {
      const invoiceData: InvoiceData = {
        id: inv.id,
        userName: currentUser?.full_name || 'Valued Customer',
        userEmail: currentUser?.email || '',
        amount: inv.amount,
        currency: inv.currency,
        status: inv.status || 'Paid',
        plan: inv.plan,
        date: inv.billing_date
      };

      // El generador ahora llama internamente a doc.save()
      generateInvoicePdf(invoiceData);

      sileo.success({
        title: 'Invoice Generated',
        description: 'Your invoice PDF is being downloaded.',
        icon: <Download className="size-3.5" />
      });
    } catch (err) {
      console.error('Failed to generate invoice:', err);
      sileo.error({
        title: 'Download Error',
        description: 'Could not generate the invoice PDF.'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-text-primary font-mono">{t('settings.title')}</h1>
          <p className="text-text-muted font-mono mt-2">
            {t('settings.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card padding="sm" corners="asymmetric">
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = selectedSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setSelectedSection(section.id)}
                      className={`
                        w-full flex items-center gap-3 px-4 py-3 rounded-asymmetric-md
                        font-mono text-sm transition-all duration-200
                        ${isActive
                          ? 'bg-primary text-text-inverse'
                          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                        }
                      `}
                    >
                      <Icon className="h-4 w-4" />
                      {section.label}
                    </button>
                  );
                })}
              </nav>
            </Card>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3 space-y-6">
            {/* Profile Section */}
            {selectedSection === 'profile' && (
              <Card padding="lg" corners="asymmetric">
                <CardHeader>
                  <CardTitle>{t('settings.profile.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {isUserLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      <span className="ml-3 text-text-secondary font-mono">{t('settings.profile.loadingProfile')}</span>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Avatar */}
                      <div className="flex items-center gap-6">
                        <div className="relative group">
                          <div className="h-20 w-20 rounded-asymmetric-lg bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center overflow-hidden">
                            {(avatarUrl || currentUser?.avatar_url) ? (
                              <img
                                src={avatarUrl || currentUser?.avatar_url || ''}
                                alt="Avatar"
                                className="h-20 w-20 rounded-asymmetric-lg object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                }}
                              />
                            ) : (
                              <User className="h-10 w-10 text-text-inverse" />
                            )}
                          </div>
                          {/* Camera overlay to change avatar */}
                          <button
                            type="button"
                            onClick={() => setShowAvatarInput(!showAvatarInput)}
                            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-asymmetric-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
                            title="Change avatar"
                          >
                            <Camera className="h-6 w-6 text-white" />
                          </button>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-text-primary font-mono">
                            {userData.name}
                          </h3>
                          <p className="text-sm text-text-muted font-mono">{userData.email}</p>
                          <Badge severity={currentUser?.tier !== 'free' ? 'success' : 'low'} className="mt-2 capitalize">
                            {currentUser?.tier ?? 'free'} {t('settings.profile.proPlan')}
                          </Badge>
                        </div>
                      </div>

                      {/* Avatar URL Input */}
                      {showAvatarInput && (
                        <div className="rounded-asymmetric-md border border-border bg-surface p-4 space-y-3">
                          <label className="block text-sm font-medium text-text-primary font-mono">
                            <LinkIcon className="inline h-4 w-4 mr-2" />
                            {t('settings.profile.avatarUrl', 'Profile image URL')}
                          </label>
                          <Input
                            value={avatarUrl}
                            onChange={(e) => setAvatarUrl(e.target.value)}
                            placeholder="https://example.com/my-avatar.jpg"
                          />
                          {avatarUrl && (
                            <div className="flex items-center gap-3">
                              <div className="h-12 w-12 rounded-asymmetric-md overflow-hidden bg-surface-hover flex items-center justify-center">
                                <img
                                  src={avatarUrl}
                                  alt="Preview"
                                  className="h-12 w-12 object-cover"
                                  onError={(e) => { (e.target as HTMLImageElement).src = ''; }}
                                />
                              </div>
                              <span className="text-xs text-text-muted font-mono">{t('settings.profile.avatarPreview', 'Live preview')}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Form Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                            {t('settings.profile.fullName')}
                          </label>
                          <Input
                            value={userData.name}
                            onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                            {t('settings.profile.email')}
                          </label>
                          <Input
                            type="email"
                            value={userData.email}
                            readOnly
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                            {t('settings.profile.company')}
                          </label>
                          <Input
                            value={userData.company}
                            onChange={(e) => setUserData({ ...userData, company: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                            {t('settings.profile.role')}
                          </label>
                          <Input
                            value={userData.role}
                            readOnly
                          />
                        </div>
                        <div className="md:col-span-2 space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                              GitHub Personal Access Token (PAT)
                            </label>
                            <Input
                              type="password"
                              placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                              value={userData.githubToken}
                              onChange={(e) => setUserData({ ...userData, githubToken: e.target.value })}
                            />
                            <p className="mt-2 text-xs text-text-muted font-mono">
                              Used to analyze private repositories and comment on Pull Requests. 
                              <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                                Generate one here
                              </a> (Needs 'repo' scope).
                            </p>
                          </div>

                          <div className="pt-6 border-t border-border">
                            <h3 className="text-sm font-bold text-primary font-mono uppercase tracking-widest mb-4 flex items-center gap-2">
                              AI Configuration (Gemini)
                              {currentUser?.tier === 'free' && <Badge severity="low" size="sm" className="ml-2 font-mono">Pro Feature</Badge>}
                            </h3>
                            
                            {currentUser?.tier === 'free' ? (
                                <div className="bg-surface-hover/50 border border-border/50 rounded-asymmetric-md p-6 text-center">
                                    <Crown className="h-8 w-8 text-primary/50 mx-auto mb-3" />
                                    <h4 className="text-sm font-bold text-text-primary font-mono mb-1">Unlock Deep AI Analysis</h4>
                                    <p className="text-xs text-text-muted font-mono mb-4">Gemini-powered reasoning for your code is only available on paid plans.</p>
                                    <Button variant="outline" size="sm" onClick={() => setSelectedSection('billing')}>
                                        View Plans
                                    </Button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-500">
                                    <div>
                                        <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                                        Google Gemini API Key
                                        </label>
                                        <Input
                                        type="password"
                                        placeholder="AIzaSy..."
                                        value={userData.geminiApiKey}
                                        onChange={(e) => setUserData({ ...userData, geminiApiKey: e.target.value })}
                                        />
                                        <p className="mt-2 text-[10px] text-text-muted font-mono">
                                        Your personal key for deep analysis. If empty, the system will use the default key.
                                        <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                                            Get key here
                                        </a>.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                                        Preferred Model
                                        </label>
                                        <select
                                        value={userData.geminiModel}
                                        onChange={(e) => setUserData({ ...userData, geminiModel: e.target.value })}
                                        className="
                                            w-full px-4 py-3 rounded-asymmetric-md
                                            bg-surface border border-border
                                            text-text-primary font-mono text-sm
                                            focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                                            transition-all duration-200
                                            cursor-pointer
                                        "
                                        >
                                        <option value="gemini-3-flash">Gemini 3 Flash (Fastest & Latest)</option>
                                        <option value="gemini-3.1-pro">Gemini 3.1 Pro (Highest Reasoning)</option>
                                        <option value="gemini-3.1-flash-lite">Gemini 3.1 Flash Lite (Economic)</option>
                                        <option value="gemini-2.5-pro">Gemini 2.5 Pro (Balanced)</option>
                                        <option value="gemini-2.5-flash">Gemini 2.5 Flash (Efficient)</option>
                                        <option value="gemini-2.0-flash">Gemini 2.0 Flash (Stable)</option>
                                        </select>
                                        <p className="mt-2 text-[10px] text-text-muted font-mono">
                                        Flash models are recommended for code analysis.
                                        </p>
                                    </div>
                                </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 pt-4">
                        <Button
                          variant="primary"
                          corners="asymmetric"
                          icon={updateProfile.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                          onClick={handleSaveProfile}
                          disabled={updateProfile.isPending}
                        >
                          {updateProfile.isPending ? t('settings.profile.saving') : t('settings.profile.saveChanges')}
                        </Button>
                        <Button variant="ghost" corners="asymmetric-inverse">
                          {t('settings.profile.cancel')}
                        </Button>
                        {updateProfile.isSuccess && (
                          <span className="text-sm text-success font-mono">✓ {t('settings.profile.profileUpdated')}</span>
                        )}
                        <Button
                          variant="ghost"
                          corners="asymmetric"
                          onClick={logout}
                          className="ml-auto text-error hover:text-error hover:bg-error/10"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          {t('settings.profile.logout', 'Cerrar sesión')}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Billing Section */}
            {selectedSection === 'billing' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                {/* Current Plan Card - Matched with Dashboard Stats style */}
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-primary">
                      <Zap className="h-4 w-4" />
                      <span className="text-xs font-mono uppercase tracking-wide">Current Plan</span>
                      {currentUser?.tier !== 'free' && (
                        <span className="ml-2 px-2 py-0.5 rounded-md bg-primary/20 text-[10px] font-bold uppercase tracking-widest">Active</span>
                      )}
                    </div>
                    <div className="text-4xl font-bold font-mono text-text-primary capitalize leading-tight">
                      {currentUser?.tier || 'Free'}
                    </div>
                    <p className="text-sm text-text-muted font-mono mt-1 whitespace-normal break-words">
                      {currentUser?.tier === 'free' 
                        ? 'You are currently on the basic plan. Upgrade to unlock deep AI analysis and higher limits.'
                        : `You have full access to ${currentUser?.tier} features including Gemini Deep Analysis.`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {currentUser?.tier === 'free' ? (
                      <Button variant="primary" corners="asymmetric-inverse" onClick={() => handleUpgrade('pro')} disabled={upgradePlan.isPending}>
                        {upgradePlan.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                        Upgrade to Pro
                      </Button>
                    ) : (
                      <Button variant="ghost" corners="asymmetric" className="text-text-muted border border-border/50">
                        Manage Subscription
                      </Button>
                    )}
                  </div>
                </div>

                {/* Plan Comparison - Minimal Claude Style */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { id: 'free', name: 'Free', price: '$0', features: ['10 Projects', 'Quick SAST Analysis', 'Community Support'] },
                    { id: 'pro', name: 'Pro', price: '$19', features: ['Unlimited Projects', 'Gemini Deep Analysis', 'Priority Support', 'Custom Rules'] },
                    { id: 'enterprise', name: 'Enterprise', price: '$99', features: ['SAML/SSO', 'Dedicated GPU Node', 'Custom AI Training', 'Audit Logs'] }
                  ].map((p) => (
                    <Card key={p.id} padding="md" corners="asymmetric" className={`transition-all border-border/50 ${currentUser?.tier === p.id ? 'ring-2 ring-primary border-transparent' : 'hover:border-primary/30'}`}>
                      <div className="space-y-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold text-text-primary font-mono uppercase tracking-tighter text-lg">{p.name}</h4>
                            <div className="flex items-baseline gap-1 mt-1">
                              <span className="text-2xl font-bold text-text-primary font-mono">{p.price}</span>
                              <span className="text-[10px] text-text-muted font-mono uppercase">/ month</span>
                            </div>
                          </div>
                          {currentUser?.tier === p.id && <Check className="h-5 w-5 text-primary" />}
                        </div>
                        <ul className="space-y-2.5">
                          {p.features.map(f => (
                            <li key={f} className="flex items-start gap-2 text-xs text-text-secondary font-mono">
                              <Check className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        {currentUser?.tier !== p.id && (
                          <Button 
                            variant={p.id === 'free' ? 'ghost' : 'outline'} 
                            size="sm" 
                            className="w-full mt-2" 
                            onClick={() => handleUpgrade(p.id as any)}
                            disabled={upgradePlan.isPending}
                          >
                            {p.id === 'free' ? 'Downgrade' : `Choose ${p.name}`}
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Payment History - Claude Minimalist Style */}
                <div className="pt-8">
                  <h3 className="text-sm font-bold text-text-primary font-mono uppercase tracking-widest mb-4">Payment History</h3>
                  <Card padding="none" corners="sharp" className="overflow-hidden border-border/30 bg-surface/20">
                    {isInvoicesLoading ? (
                      <div className="p-12 text-center text-text-muted font-mono"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading history...</div>
                    ) : invoices && invoices.length > 0 ? (
                      <table className="w-full text-left border-collapse font-mono text-[11px]">
                        <thead className="bg-surface-hover/50 border-b border-border/50 uppercase tracking-tighter opacity-50">
                          <tr>
                            <th className="px-6 py-4 font-bold">Date</th>
                            <th className="px-6 py-4 font-bold">Plan</th>
                            <th className="px-6 py-4 font-bold">Amount</th>
                            <th className="px-6 py-4 font-bold">Status</th>
                            <th className="px-6 py-4 font-bold text-right">Invoice</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border/30">
                          {invoices.map((inv: any) => (
                            <tr key={inv.id} className="hover:bg-primary/5 transition-colors group">
                              <td className="px-6 py-4 text-text-secondary">{new Date(inv.billing_date).toLocaleDateString()}</td>
                              <td className="px-6 py-4 font-bold text-text-primary uppercase">{inv.plan}</td>
                              <td className="px-6 py-4">{inv.currency} {inv.amount.toFixed(2)}</td>
                              <td className="px-6 py-4">
                                <span className="flex items-center gap-1.5 text-success">
                                  <div className="h-1 w-1 rounded-full bg-success animate-pulse" />
                                  Paid
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <button 
                                  onClick={() => handleDownloadInvoice(inv)} 
                                  className="text-text-muted hover:text-primary transition-colors inline-flex items-center gap-1 font-bold"
                                >
                                  PDF <Download className="h-3 w-3" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="p-12 text-center text-text-muted font-mono">No invoices found.</div>
                    )}
                  </Card>
                  <p className="mt-4 text-[10px] text-text-muted font-mono flex items-center gap-2">
                    <Shield className="h-3 w-3" />
                    Payments are simulated for this MVP. No real charges will be made.
                  </p>
                </div>
              </div>
            )}

            {/* Notifications Section */}
            {selectedSection === 'notifications' && (
              <Card padding="lg" corners="asymmetric">
                <CardHeader>
                  <CardTitle>{t('settings.notifications.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <ToggleOption
                      label={t('settings.notifications.emailAlerts')}
                      description={t('settings.notifications.emailAlertsDesc')}
                      checked={notifications.emailAlerts}
                      onChange={(checked) => setNotifications({ ...notifications, emailAlerts: checked })}
                    />
                    <ToggleOption
                      label={t('settings.notifications.criticalIssues')}
                      description={t('settings.notifications.criticalIssuesDesc')}
                      checked={notifications.criticalIssues}
                      onChange={(checked) => setNotifications({ ...notifications, criticalIssues: checked })}
                    />
                    <ToggleOption
                      label={t('settings.notifications.weeklyReports')}
                      description={t('settings.notifications.weeklyReportsDesc')}
                      checked={notifications.weeklyReports}
                      onChange={(checked) => setNotifications({ ...notifications, weeklyReports: checked })}
                    />
                    <ToggleOption
                      label={t('settings.notifications.newFeatures')}
                      description={t('settings.notifications.newFeaturesDesc')}
                      checked={notifications.newFeatures}
                      onChange={(checked) => setNotifications({ ...notifications, newFeatures: checked })}
                    />

                    <div className="flex items-center gap-3 pt-4 border-t border-border">
                      <Button variant="primary" corners="asymmetric" icon={<Save className="h-4 w-4" />}>
                        {t('settings.notifications.savePreferences')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* API Keys Section */}
            {selectedSection === 'api-keys' && (
              <Card padding="lg" corners="asymmetric">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('settings.apiKeys.title')}</CardTitle>
                    <Button variant="primary" size="sm" corners="asymmetric" icon={<Key className="h-4 w-4" />}>
                      {t('settings.apiKeys.generateNew')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-sm text-text-muted font-mono">
                      {t('settings.apiKeys.description')}
                    </p>

                    {/* Sample API Key */}
                    <div className="rounded-asymmetric-md border border-border bg-surface p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-text-primary font-mono">{t('settings.apiKeys.productionKey')}</h4>
                          <p className="text-xs text-text-muted font-mono mt-1">{t('settings.apiKeys.createdOn')} Jan 15, 2026</p>
                        </div>
                        <Badge severity="low">{t('settings.apiKeys.active')}</Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <code className="flex-1 px-3 py-2 bg-background rounded-md border border-border text-sm font-mono text-text-secondary">
                          dlnt_sk_••••••••••••••••••••••••3x9f
                        </code>
                        <Button variant="ghost" size="sm" corners="asymmetric">
                          {t('settings.apiKeys.copy')}
                        </Button>
                        <Button variant="ghost" size="sm" corners="asymmetric-inverse">
                          {t('settings.apiKeys.revoke')}
                        </Button>
                      </div>
                    </div>

                    <div className="rounded-asymmetric-md border border-warning/30 bg-warning/5 p-4">
                      <p className="text-sm text-text-secondary font-mono">
                        ⚠️ <strong>{t('settings.apiKeys.securityNotice')}</strong> {t('settings.apiKeys.securityNoticeDesc')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Security Section */}
            {selectedSection === 'security' && (
              <Card padding="lg" corners="asymmetric">
                <CardHeader>
                  <CardTitle>{t('settings.security.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Password Change */}
                    <div>
                      <h3 className="text-sm font-medium text-text-primary font-mono mb-4">
                        {t('settings.security.changePassword')}
                      </h3>
                      <div className="space-y-4">
                        <Input type="password" placeholder={t('settings.security.currentPassword')} />
                        <Input type="password" placeholder={t('settings.security.newPassword')} />
                        <Input type="password" placeholder={t('settings.security.confirmPassword')} />
                        <Button variant="primary" corners="asymmetric">
                          {t('settings.security.updatePassword')}
                        </Button>
                      </div>
                    </div>

                    {/* Two-Factor Authentication */}
                    <div className="pt-6 border-t border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-text-primary font-mono">
                            {t('settings.security.twoFactor')}
                          </h3>
                          <p className="text-sm text-text-muted font-mono mt-1">
                            {t('settings.security.twoFactorDesc')}
                          </p>
                        </div>
                        <Badge severity="medium">{t('settings.security.notEnabled')}</Badge>
                      </div>
                      <Button variant="outline" corners="asymmetric" className="mt-4">
                        {t('settings.security.enable2FA')}
                      </Button>
                    </div>

                    {/* Active Sessions */}
                    <div className="pt-6 border-t border-border">
                      <h3 className="text-sm font-medium text-text-primary font-mono mb-4">
                        {t('settings.security.activeSessions')}
                      </h3>
                      <div className="rounded-asymmetric-md border border-border bg-surface p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-text-primary font-mono">
                              {t('settings.security.currentSession')}
                            </p>
                            <p className="text-xs text-text-muted font-mono mt-1">
                              Windows • Chrome • {userData.timezone}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            corners="asymmetric"
                            icon={<LogOut className="h-4 w-4" />}
                            onClick={logout}
                          >
                            {t('settings.security.signOut')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Preferences Section */}
            {selectedSection === 'preferences' && (
              <Card padding="lg" corners="asymmetric">
                <CardHeader>
                  <CardTitle>{t('settings.preferences.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">

                    {/* Language */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                        {t('settings.preferences.language')}
                      </label>
                      <select
                        value={(() => {
                          const lang = i18n.language;
                          if (lang?.startsWith('zh')) return 'zh';
                          if (lang?.startsWith('hi')) return 'hi';
                          if (lang?.startsWith('fr')) return 'fr';
                          if (lang?.startsWith('pt')) return 'pt';
                          if (lang?.startsWith('es')) return 'es';
                          return 'en';
                        })()}
                        onChange={(e) => {
                          i18n.changeLanguage(e.target.value);
                        }}
                        className="
                          w-full px-4 py-3 rounded-asymmetric-md
                          bg-surface border border-border
                          text-text-primary font-mono text-sm
                          focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                          transition-all duration-200
                          cursor-pointer
                        "
                      >
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="zh">中文 (Chinese)</option>
                        <option value="hi">हिन्दी (Hindi)</option>
                        <option value="fr">Français (French)</option>
                        <option value="pt">Português (Portuguese)</option>
                      </select>
                    </div>

                    {/* Timezone */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                        {t('settings.preferences.timezone')}
                      </label>
                      <Input value={userData.timezone} readOnly />
                    </div>

                    <div className="flex items-center gap-3 pt-4 border-t border-border">
                      <span className="text-sm text-success font-mono">
                        ✓ {t('settings.preferences.autoSaved')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// Toggle component for notifications
function ToggleOption({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1">
        <p className="text-sm font-medium text-text-primary font-mono">{label}</p>
        <p className="text-xs text-text-muted font-mono mt-1">{description}</p>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`
          relative inline-flex h-6 w-11 items-center rounded-full transition-colors
          ${checked ? 'bg-primary' : 'bg-surface-hover'}
        `}
      >
        <span
          className={`
            inline-block h-4 w-4 transform rounded-full transition-transform
            ${checked ? 'translate-x-6 bg-black' : 'translate-x-1 bg-white'}
          `}
        />
      </button>
    </div>
  );
}
